import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import OrderService from '../services/orderService';
import { Order } from '../types';
import AuthService from '../services/authService';
import moment from 'moment';
import { formatUTCToLocal } from '../utils/timezoneUtils';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    loadOrder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (copyMessage) {
      const timer = setTimeout(() => {
        setCopyMessage(null);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [copyMessage]);

  const handleCopy = (text: string, field: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyMessage(`Đã copy ${field}`);
        setTimeout(() => setCopyMessage(null), 2000);
      })
      .catch(err => {
        console.error('Không thể sao chép: ', err);
        setCopyMessage('Lỗi khi sao chép');
      });
  };

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await OrderService.getById(Number(id));
      setOrder(response.data);
      setError(null);
    } catch (err) {
      setError('Không thể tải thông tin đơn hàng. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Bạn có chắc chắn muốn xóa đơn hàng này?')) {
      try {
        await OrderService.delete(Number(id));
        navigate(`${userPrefix}/orders`);
      } catch (err) {
        setError('Không thể xóa đơn hàng. Vui lòng thử lại sau.');
        console.error(err);
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge bg-warning">Đang xử lý</span>;
      case 'preparing':
        return <span className="badge bg-primary">Đang chuẩn bị</span>;
      case 'completed':
        return <span className="badge bg-success">Hoàn thành</span>;
      case 'done':
        return <span className="badge" style={{ backgroundColor: '#C9C1EC', color: '#1f1f1f' }}>Đã xong</span>;
      case 'cancelled':
        return <span className="badge bg-danger">Đã hủy</span>;
      default:
        return <span className="badge bg-secondary">Không xác định</span>;
    }
  };

  const getRemainingAmount = () => {
    if (!order) return 0;
    return order.totalValue - order.deposit;
  };

  const getImageUrl = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined;
    return OrderService.getImageUrl(imagePath) || undefined;
  };

  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner-border" role="status" aria-label="loading"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <button
          className="btn btn-primary"
          onClick={() => navigate(`${userPrefix}/orders`)}
        >
          Trở về danh sách
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Không tìm thấy thông tin đơn hàng.</div>
        <button
          className="btn btn-primary"
          onClick={() => navigate(`${userPrefix}/orders`)}
        >
          Trở về danh sách
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Chi tiết đơn hàng #{order.id}</h2>
        <div>
          <Link
            to={`${userPrefix}/orders/edit/${id}`}
            className="btn btn-warning me-2"
          >
            Sửa
          </Link>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
          >
            Xóa
          </button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Thông tin đơn hàng</div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>Sản phẩm:</strong> {order.product}
              </p>
              <p>
                <strong>Số lượng:</strong> {order.quantity}
              </p>
              <p>
                <strong>Đơn giá:</strong> {order.price.toLocaleString()}đ
              </p>
              
              <p>
                <strong>Tổng giá trị:</strong> {order.totalValue.toLocaleString()}đ
              </p>
              <p>
                <strong>Tiền đặt cọc:</strong> {order.deposit.toLocaleString()}đ
              </p>
              <p>
                <strong>Còn lại:</strong> {getRemainingAmount().toLocaleString()}đ
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Ngày giao hàng:</strong>{' '}
                {formatUTCToLocal(order.deliveryDate, 'DD/MM/YYYY HH:mm')}
              </p>
              <p>
                <strong>Trạng thái:</strong> {getStatusBadge(order.status)}
              </p>
              <p>
                <strong>Ngày tạo:</strong>{' '}
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString()
                  : 'N/A'}
              </p>
              <p>
                <strong>Cập nhật lần cuối:</strong>{' '}
                {order.updatedAt
                  ? new Date(order.updatedAt).toLocaleString()
                  : 'N/A'}
              </p>
              {order.address && (
                <p>
                  <strong>Địa chỉ:</strong> {order.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {order.imagePath && (
        <div className="card mb-4">
          <div className="card-header">Hình ảnh</div>
          <div className="card-body text-center">
            <img 
              src={getImageUrl(order.imagePath)} 
              alt="Hình ảnh đơn hàng" 
              className="img-fluid" 
              style={{ maxHeight: '400px' }} 
            />
          </div>
        </div>
      )}

      {order.notes && (
        <div className="card mb-4">
          <div className="card-header">Ghi chú</div>
          <div className="card-body">
            <p>{order.notes}</p>
          </div>
        </div>
      )}

      {order.remarks && (
        <div className="card mb-4">
          <div className="card-header">Lưu ý</div>
          <div className="card-body">
            <p>{order.remarks}</p>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">Thông tin khách hàng</div>
        <div className="card-body">
          {order.customer ? (
            <div>
              <p>
                <strong>ID:</strong>{' '}
                <Link to={`${userPrefix}/customers/${order.customerId}`}>{order.customerId}</Link>
              </p>
              <p>
                <strong>Tên:</strong> {order.customer.name || 'Chưa có thông tin'}
              </p>
              <p>
                <strong>Số điện thoại:</strong>{' '}
                <span className="copyable" onClick={handleCopy(order.customer.phoneNumber || '', 'số điện thoại')}>
                  {order.customer.phoneNumber}{' '}
                  <i className="fas fa-copy text-primary ms-1"></i>
                </span>
                {copyMessage && <small className="text-success ms-2">{copyMessage}</small>}
              </p>
              {order.customer.address && (
                <p>
                  <strong>Địa chỉ:</strong> {order.customer.address}
                </p>
              )}
              <Link to={`${userPrefix}/customers/${order.customerId}`} className="btn btn-outline-primary btn-sm mt-2">
                Xem chi tiết khách hàng
              </Link>
            </div>
          ) : (
            <p>Không có thông tin khách hàng.</p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Link to={`${userPrefix}/orders`} className="btn btn-secondary">
          <i className="fas fa-arrow-left mr-1"></i> Quay lại
        </Link>
      </div>
    </div>
  );
};

export default OrderDetail; 